import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "elements/Box";
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Chip } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PiCheckCircleFill, PiCircle } from 'react-icons/pi';

import "assets/css/main.css";
import { ApiRequest } from "GlobalFunctions";

/*=========================================================
PHASES CONFIG
=========================================================*/
const ALL_PHASES = [
  { key: 1, label: 'Bodega', color: '#E53935', dataKey: 'bodega' },
  { key: 2, label: 'Certificación', color: '#FB8C00', dataKey: 'certificacion' },
  { key: 3, label: 'Conductor', color: '#43A047', dataKey: 'conductor' },
];

/*=========================================================
PICKING STATUS BAR (same as list)
=========================================================*/
function PickingStatusBar({ guideStatus, pickingCertification = true }: { guideStatus: number; pickingCertification?: boolean }) {
  const PHASES = pickingCertification ? ALL_PHASES : ALL_PHASES.filter(p => p.key !== 2);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      {PHASES.map((phase) => {
        const isCompleted = guideStatus > phase.key;
        const isActive = guideStatus === phase.key;
        const color = isCompleted || isActive ? phase.color : '#bdbdbd';

        return (
          <Tooltip
            key={phase.key}
            title={isCompleted ? `${phase.label}: Completado` : isActive ? `${phase.label}: En progreso` : `${phase.label}: Pendiente`}
            arrow
            placement="top"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
              {isCompleted
                ? <PiCheckCircleFill size={20} color={phase.color} />
                : <PiCircle size={20} color={color} />
              }
              <span style={{ fontSize: '13px', fontWeight: isActive ? 700 : 400, color }}>
                {phase.label}
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

/*=========================================================
TRAFFIC LIGHT (inline, like the app)
=========================================================*/
function TrafficLight({ checksData, pickingCertification = true }: { checksData: any; pickingCertification?: boolean }) {
  const allLights = [
    { checked: checksData?.bodega?.checked, color: '#E53935', label: 'Bodega', key: 'bodega' },
    { checked: checksData?.certificacion?.checked, color: '#FB8C00', label: 'Certificación', key: 'certificacion' },
    { checked: checksData?.conductor?.checked, color: '#43A047', label: 'Conductor', key: 'conductor' },
  ];
  const lights = pickingCertification ? allLights : allLights.filter(l => l.key !== 'certificacion');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {lights.map((light, i) => (
        <Tooltip key={i} title={`${light.label}: ${light.checked ? 'Verificado' : 'Pendiente'}`} arrow>
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: `2px solid ${light.color}`,
              backgroundColor: light.checked ? light.color : 'transparent',
              transition: 'background-color 0.3s',
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

/*=========================================================
PICKING DETAILS
=========================================================*/
export default function PickingDetails(props: any) {
  const { setLoading } = props;
  const { guide_code } = useParams();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [guideStatus, setGuideStatus] = useState(1);
  const [guideComments, setGuideComments] = useState<Record<string, string>>({});
  const [phaseUsers, setPhaseUsers] = useState<Record<string, { user_id: number | null; name: string | null }>>({});
  const [pickingCertification, setPickingCertification] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (guide_code) {
      setLoading(true);
      loadProducts();
    }
  }, [guide_code]);

  const loadProducts = async () => {
    setLoading(true);

    await ApiRequest({
      url: '/picking/guideProductsWeb',
      method: 'GET',
      query: { guide_code },
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setSuppliers(response.suppliers || []);
          setGuideStatus(response.guide_status || 1);
          setGuideComments(response.guide_comments || {});
          if (response.phase_users) setPhaseUsers(response.phase_users);
          if (response.picking_certification !== undefined) {
            setPickingCertification(response.picking_certification);
          }
        }
        setLoading(false);
      }
    });
  };

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: return 'Bodega';
      case 2: return 'Certificación';
      case 3: return 'Conductor';
      case 4: return 'Finalizado';
      default: return '-';
    }
  };

  return (
    <div id="PickingDetails" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">
              <IconButton
                onClick={() => navigate('/picking')}
                style={{ marginRight: '10px' }}
              >
                <ArrowBackIcon />
              </IconButton>
              Guía: {guide_code}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Progreso:</span>
              <PickingStatusBar guideStatus={guideStatus} pickingCertification={pickingCertification} />
              {guideStatus === 4 && (
                <Chip label="Finalizado" color="success" size="small" />
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', width: '100%', marginBottom: '10px' }}>
          <Box className="box100" style={{ flex: '0 0 40%', padding: '14px 16px' }}>
            <span style={{ fontWeight: 600, fontSize: '13px', color: '#333', display: 'block', marginBottom: '6px' }}>Usuarios</span>
            {(phaseUsers.bodega?.name || (pickingCertification && phaseUsers.certificacion?.name) || phaseUsers.conductor?.name) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {phaseUsers.bodega?.name && (
                  <span style={{ fontSize: '12px', color: '#444' }}>
                    <span style={{ color: '#E53935', fontWeight: 600 }}>Bodega:</span> {phaseUsers.bodega.name}
                  </span>
                )}
                {pickingCertification && phaseUsers.certificacion?.name && (
                  <span style={{ fontSize: '12px', color: '#444' }}>
                    <span style={{ color: '#FB8C00', fontWeight: 600 }}>Certificación:</span> {phaseUsers.certificacion.name}
                  </span>
                )}
                {phaseUsers.conductor?.name && (
                  <span style={{ fontSize: '12px', color: '#444' }}>
                    <span style={{ color: '#43A047', fontWeight: 600 }}>Conductor:</span> {phaseUsers.conductor.name}
                  </span>
                )}
              </div>
            ) : (
              <span style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>Sin usuarios</span>
            )}
          </Box>
          <Box className="box100" style={{ flex: '0 0 calc(60% - 16px)', padding: '14px 16px' }}>
            {(() => {
              const COMMENT_PHASES = [
                { key: 'bodega', label: 'Bodega', color: '#E53935' },
                ...(pickingCertification ? [{ key: 'certificacion', label: 'Certificación', color: '#FB8C00' }] : []),
                { key: 'conductor', label: 'Conductor', color: '#43A047' },
              ];
              const withComments = COMMENT_PHASES.filter(p => guideComments[p.key]);
              return (
                <>
                  <span style={{ fontWeight: 600, fontSize: '13px', color: '#333', display: 'block', marginBottom: '6px' }}>Comentarios</span>
                  {withComments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {withComments.map(p => (
                        <span key={p.key} style={{ fontSize: '13px', color: '#444' }}>
                          <span style={{ color: p.color, fontWeight: 600 }}>{p.label}:</span>
                          {' '}{guideComments[p.key]}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>Sin comentarios</span>
                  )}
                </>
              );
            })()}
          </Box>
        </div>

        {suppliers.map((supplier, si) => (
          <Box key={si} id={`supplier-${si}`} className="box100" style={{ marginBottom: '16px' }}>
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              borderRadius: '8px 8px 0 0',
            }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                {supplier.supplier_name}
              </span>
              <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                ({supplier.products.length} productos)
              </span>
            </div>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px' }}>Producto</TableCell>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px' }}>Despacho</TableCell>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px', textAlign: 'center' }}>Cajas</TableCell>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px', textAlign: 'center' }}>Display</TableCell>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px', textAlign: 'center' }}>Unidades</TableCell>
                    <TableCell align="center" sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px' }}>Estado</TableCell>
                    <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", fontSize: '12px' }}>Comentarios</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supplier.products.map((product: any, pi: number) => {
                    const cd = product.checks_data || {};
                    const comments: { label: string; color: string; text: string }[] = [];
                    if (cd.bodega?.comment) comments.push({ label: 'Bodega', color: '#E53935', text: cd.bodega.comment });
                    if (pickingCertification && cd.certificacion?.comment) comments.push({ label: 'Certificación', color: '#FB8C00', text: cd.certificacion.comment });
                    if (cd.conductor?.comment) comments.push({ label: 'Conductor', color: '#43A047', text: cd.conductor.comment });

                    return (
                      <TableRow key={pi} hover>
                        <TableCell>
                          <span className='flex_title'>{product.product_name}</span>
                          <span className='flex_subtitle'>{product.product_code}</span>
                        </TableCell>
                        <TableCell>{product.despacho || '-'}</TableCell>
                        <TableCell align="center">{product.cajas || 0}</TableCell>
                        <TableCell align="center">{product.display || 0}</TableCell>
                        <TableCell align="center">{product.unidades || 0}</TableCell>
                        <TableCell align="center">
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <TrafficLight checksData={cd} pickingCertification={pickingCertification} />
                          </div>
                        </TableCell>
                        <TableCell>
                          {comments.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {comments.map((c, ci) => (
                                <span key={ci} style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                  <span style={{ color: c.color, fontWeight: 600 }}>{c.label}:</span>{' '}
                                  <span style={{ color: '#666' }}>{c.text}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#ccc', fontSize: '12px' }}>-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        {suppliers.length > 0 && (() => {
          let totalProductos = 0, totalDespacho = 0, totalCajas = 0, totalDisplay = 0, totalUnidades = 0;
          suppliers.forEach((s: any) => s.products.forEach((p: any) => {
            totalProductos++;
            totalDespacho += Number(p.despacho) || 0;
            totalCajas += Number(p.cajas) || 0;
            totalDisplay += Number(p.display) || 0;
            totalUnidades += Number(p.unidades) || 0;
          }));
          return (
            <Box className="box100" style={{ marginBottom: '16px' }}>
              <div style={{ padding: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>Totales</span>
                <span style={{ fontSize: '13px', color: '#555' }}>Productos: <strong>{totalProductos}</strong></span>
                <span style={{ fontSize: '13px', color: '#555' }}>Despacho: <strong>{totalDespacho}</strong></span>
                <span style={{ fontSize: '13px', color: '#555' }}>Cajas: <strong>{totalCajas}</strong></span>
                <span style={{ fontSize: '13px', color: '#555' }}>Display: <strong>{totalDisplay}</strong></span>
                <span style={{ fontSize: '13px', color: '#555' }}>Unidades: <strong>{totalUnidades}</strong></span>
              </div>
            </Box>
          );
        })()}

        {suppliers.length === 0 && (
          <Box id="PickingEmpty" className="box100">
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              No hay productos en esta guía
            </div>
          </Box>
        )}
      </div>
    </div>
  );
}
